import { Component, ViewChild } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ViewDidEnter, ViewWillEnter, ViewWillLeave } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { ServerDto } from "../../../core/dto/server.dto";
import { CURRENT_SERVER_CONFIGURATION, ServersConnectionsProvider } from "../../../core/providers/serversConnections.provider";
import { DefaultDataDownloaderService } from "../../../core/services/defaultDataDownloader.service";
import { UserService } from "../../../core/services/user.service";
import { environment } from "../../../environments/environment";
import { IriumInputComponent } from "../../../gyzmo-commons/components/irium-input/irium-input";
import { KeyValueDbDao } from "../../../gyzmo-commons/dao/db/keyValue.db.dao";
import { LoadingHelper } from "../../../gyzmo-commons/helpers/loading.helper";
import { NavControllerExtended } from "../../../gyzmo-commons/helpers/navControllerExtended";
import { NetworkHelper } from "../../../gyzmo-commons/helpers/network.helper";
import { isNullOrEmpty } from "../../../gyzmo-commons/helpers/null.helper";
import { ToastHelper } from "../../../gyzmo-commons/helpers/toast.helper";
import { KeyValue } from "../../../gyzmo-commons/models/keyValue.model";
import { AppSqlProvider } from "../../../gyzmo-commons/persistence/app.sql.provider";
import { LoggerService } from "../../../gyzmo-commons/services/logs/logger.service";

@Component({
    selector: "page-connection",
    templateUrl: "connection.page.html",
    styleUrls: ["connection.page.scss"],
})
export class ConnectionPage implements ViewWillEnter, ViewWillLeave, ViewDidEnter {
    @ViewChild("loginField", { static: false }) loginField: IriumInputComponent;
    @ViewChild("passwordField", { static: false }) passwordField: IriumInputComponent;

    connectionForm: FormGroup = null;

    isConnected = false;
    errorMsg = "";
    server = "";
    activeServerConnection: ServerDto = new ServerDto();
    private connectivitySubscription: Subscription;

    constructor(private navControllerExtended: NavControllerExtended,
                private appSqlProvider: AppSqlProvider,
                private serversConnectionsProvider: ServersConnectionsProvider,
                private logger: LoggerService,
                private toastHelper: ToastHelper,
                private defaultDataDownloaderService: DefaultDataDownloaderService,
                private keyValueDbDao: KeyValueDbDao,
                private loadingHelper: LoadingHelper,
                private networkHelper: NetworkHelper,
                private userService: UserService,
                private translateService: TranslateService) {
    }

    async ionViewWillEnter() {
        this.isConnected = this.networkHelper.isConnected();
        this.connectivitySubscription = this.networkHelper.getConnectivityObservable()
            .subscribe((isConnected) => {
                this.isConnected = isConnected;
                if (!isConnected) {
                    this.errorMsg = this.translateService.instant("IMCP_Connection_error_please_check_that_you_are_under_network_coverage.");
                } else {
                    this.errorMsg = "";
                }
            });

        let currentServerConfiguration = await this.keyValueDbDao.get(CURRENT_SERVER_CONFIGURATION);
        if (!currentServerConfiguration) {
            this.activeServerConnection = new ServerDto();
            this.activeServerConnection.url = environment.servers[0].url;
            this.activeServerConnection.name = environment.servers[0].name;
            this.activeServerConnection.apiKey = environment.servers[0].apiKey;
            this.activeServerConnection.context = environment.servers[0].context;
        } else {
            let currentServerConfigurationObject = JSON.parse(currentServerConfiguration.value);
            this.activeServerConnection = new ServerDto();
            this.activeServerConnection.url = currentServerConfigurationObject.url;
            this.activeServerConnection.name = currentServerConfigurationObject.name;
            this.activeServerConnection.apiKey = currentServerConfigurationObject.apiKey;
            this.activeServerConnection.context = currentServerConfigurationObject.context;
        }

        let login = "";
        let password = "";
        if (this.navControllerExtended.params.hasParam("login")) {
            login = this.navControllerExtended.params.getParam("login") as string;
        }

        let lastLoginKeyValue = await this.keyValueDbDao.get("LAST_LOGIN");
        if (lastLoginKeyValue) {
            login = lastLoginKeyValue.value;
        }

        if (!isNullOrEmpty(environment.defaultLogin)) {
            login = environment.defaultLogin;
        }
        if (!isNullOrEmpty(environment.defaultPassword)) {
            password = environment.defaultPassword;
        }

        let controls: any = {};

        controls.login = new FormControl(login, [Validators.required]);
        controls.password = new FormControl(password, [Validators.required]);

        this.connectionForm = new FormGroup(controls);
    }

    ionViewWillLeave() {
        this.connectivitySubscription.unsubscribe();
    }

    ionViewDidEnter() {
        setTimeout(() => {
            let login = this.connectionForm.get("login").value;
            let password = this.connectionForm.get("password").value;
            if (isNullOrEmpty(login)) {
                this.loginField.setFocus();
            } else if (isNullOrEmpty(password)) {
                this.passwordField.setFocus();
            }
        }, 150);
    }

    public async onConnectionClick() {
        if (this.connectionForm.invalid) {
            this.connectionForm.markAllAsTouched();
        } else {
            await this.keyValueDbDao.save(new KeyValue(CURRENT_SERVER_CONFIGURATION, JSON.stringify(this.activeServerConnection)));
            await this.serversConnectionsProvider.setServerConnection(this.activeServerConnection);

            this.errorMsg = "";
            await this.loadingHelper.showLoading();
            let t0 = performance.now();

            let login = this.connectionForm.get("login").value;
            let password = this.connectionForm.get("password").value;
            this.userService.connect(login, password)
                .then(async user => {
                    this.appSqlProvider.enableBulkWriting();
                    await this.defaultDataDownloaderService.onConnectionDefaultData(user);
                    await this.appSqlProvider.commitBulk();

                    let result: { correctness: boolean; message: string } = await this.defaultDataDownloaderService.checkDefaultData();
                    if (!result.correctness) {
                        await this.toastHelper.show(this.translateService.instant("IMCP_Problème_de_synchronisation_veuillez_vous_déconnecter_et_reconnecter.") + " (" + result.message + ")");
                    }

                    await this.keyValueDbDao.save(new KeyValue("LAST_LOGIN", user.username));

                    let t1 = performance.now();
                    this.logger.info(this.constructor.name, "Connection and default data download took " + Math.floor(t1 - t0) + "ms.");

                    let cguAccepted = await this.keyValueDbDao.get("CGU_" + user.username);

                    if (cguAccepted == null || cguAccepted.value != "1") {
                        await this.navControllerExtended.navigateRoot("/cgu");
                    } else {
                        await this.navControllerExtended.navigateRoot("/home");
                    }

                    await this.loadingHelper.hideAll();
                })
                .catch(reason => {
                    this.connectionForm.get("password").setValue("");

                    if (reason.error) {
                        this.errorMsg = reason.error.statusCode + " - " + reason.error.error + " : " + reason.error.message;
                    } else if (reason) {
                        this.errorMsg = reason;
                    }

                    this.logger.error(this.constructor.name, reason);

                    void this.loadingHelper.hideAll();
                });
        }
    }

    public isFieldValid(field: string) {
        return this.connectionForm.get(field).touched && !this.connectionForm.get(field).valid;
    }

    public showAbout(): void {
        void this.navControllerExtended.navigateForward("/about/iMobDelivery");
    }
}
